import { useCallback, useEffect } from 'react';
import {
  eventReportSummaryText,
  type EventReportCount,
  type PanelEventReportRowDto,
} from '@icebreaker/shared';
import { Button, EmptyState, Skeleton, Table, Tag } from '@icebreaker/ui';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { fetchOrganizationReports } from '../store/organization-reports.slice';
import styles from './organization-reports.module.css';

type Translate = ReturnType<typeof useTranslation>;

/**
 * KURUM DETAYI › ETKİNLİK RAPORLARI (organizatör raporu Story 5, tasarım kare 5).
 *
 * Bitmiş etkinlik başına satır: ad, bitiş tarihi (etkinliğin diliminde), kesin kayıt,
 * check-in oranı, memnuniyet, durum. Kişi verisi yoktur; sayılar sunucuda eşikten
 * geçmiş gelir ve istemci bastırılmış sayıyı TAHMİN ETMEZ ("5'ten az", "Gösterilmez").
 * Dört hâl: dolu tablo, kesik çerçeveli boş durum, satır iskeleti, satır içi hata.
 *
 * Bölüm diğer kurum bölümlerinden BAĞIMSIZ yüklenir: raporlar düşerse künye ve
 * kredi kartları çalışmaya devam eder.
 */
export function OrganizationReportsSection({ organizationId }: { organizationId: string }) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const state = useAppSelector((root: RootState) => root.panelOrgReports);

  const load = useCallback(() => {
    dispatch(fetchOrganizationReports.request({ organizationId }));
  }, [dispatch, organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const own = state.organizationId === organizationId ? state.data : null;
  const min = own?.minCount ?? 0;

  return (
    <section className={styles.section} aria-labelledby="organization-reports-title">
      <h2 id="organization-reports-title" className={styles.title}>
        {t.t('admin.orgs.reports.title')}
      </h2>
      {own === null && state.phase === 'failed' ? (
        <div className={styles.state} role="alert">
          <p className={styles.error}>{t.t('admin.orgs.reports.loadError')}</p>
          <Button variant="secondary" size="sm" onClick={load}>
            {t.t('admin.orgs.reports.retry')}
          </Button>
        </div>
      ) : own === null ? (
        <div className={styles.loading}>
          <Skeleton label={t.t('admin.orgs.reports.loading')} lines={4} />
        </div>
      ) : own.items.length === 0 ? (
        <EmptyState
          frame="dashed"
          title={t.t('admin.orgs.reports.emptyTitle')}
          body={t.t('admin.orgs.reports.emptyBody')}
        />
      ) : (
        <>
          <div className={[styles.table, 'ib-priority-table'].join(' ')}>
            <Table aria-label={t.t('admin.orgs.reports.title')}>
              <thead>
                <tr>
                  <th>{t.t('admin.orgs.reports.colEvent')}</th>
                  <th>{t.t('admin.orgs.reports.colDate')}</th>
                  <th>{t.t('admin.orgs.reports.colRegistered')}</th>
                  <th>{t.t('admin.orgs.reports.colRate')}</th>
                  <th>{t.t('admin.orgs.reports.colSatisfaction')}</th>
                  <th>{t.t('admin.orgs.reports.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {own.items.map((row) => (
                  <ReportRow key={row.eventId} row={row} min={min} />
                ))}
              </tbody>
            </Table>
          </div>
          <p className={styles.note}>{t.t('admin.orgs.reports.thresholdNote', { min })}</p>
        </>
      )}
    </section>
  );
}

function ReportRow({ row, min }: { row: PanelEventReportRowDto; min: number }) {
  const t = useTranslation();
  const rate = countText(t, row.attendanceRatePercent, min, (value) =>
    t.t('admin.orgs.reports.ratePercent', { rate: t.formatNumber(value) }),
  );
  // Değer yoksa metni sunucunun hâli belirler: rapor, karşılaştırma ve e-postayla aynı
  // eşleme ("Anket yok", "Az yanıt, gösterilmez", "Gösterilmez"; kullanıcı kararı 30.10.2026).
  const summaryText = eventReportSummaryText(t, row.satisfactionState);
  const satisfaction =
    row.satisfaction !== null && row.satisfaction.value !== null && summaryText === null
      ? { text: t.formatNumber(row.satisfaction.value, SATISFACTION_FORMAT), muted: false }
      : { text: summaryText ?? t.t('admin.orgs.reports.hidden'), muted: true };
  const registered = countText(t, row.registered, min, (value) => t.formatNumber(value));
  return (
    <tr>
      <td className={styles.eventCell}>{row.title}</td>
      <td className={styles.dateCell}>
        {t.formatDate(row.endsAt, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: row.timezone,
        })}
      </td>
      <td className={styles.numberCell}>{registered.text}</td>
      <td className={[styles.numberCell, rate.muted ? styles.muted : ''].join(' ')}>{rate.text}</td>
      <td className={[styles.numberCell, satisfaction.muted ? styles.muted : ''].join(' ')}>
        {satisfaction.text}
      </td>
      <td>
        <Tag variant={row.status === 'final' ? 'success' : 'info'} className={styles.statusTag}>
          {t.t(
            row.status === 'final'
              ? 'admin.orgs.reports.statusFinal'
              : 'admin.orgs.reports.statusPreliminary',
          )}
        </Tag>
      </td>
    </tr>
  );
}

const SATISFACTION_FORMAT = { minimumFractionDigits: 1, maximumFractionDigits: 1 } as const;

/**
 * Sunucunun eşik kararını metne çevirir. `fewer_than_min` → "5'ten az", `hidden` →
 * "Gösterilmez" (soluk). Değer de bastırma da yoksa (kayıtsız etkinliğin oranı)
 * hücre "Gösterilmez" yazar; sıfır ya da tahmin yazılmaz.
 */
function countText(
  t: Translate,
  count: EventReportCount,
  min: number,
  format: (value: number) => string,
): { readonly text: string; readonly muted: boolean } {
  if (count.suppression === 'fewer_than_min') {
    return { text: t.t('admin.orgs.reports.fewerThanMin', { min }), muted: false };
  }
  if (count.suppression === 'hidden' || count.value === null) {
    return { text: t.t('admin.orgs.reports.hidden'), muted: true };
  }
  return { text: format(count.value), muted: false };
}
